import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { Stack } from '@mui/material_modern'
import AxleWeightsRow from 'components/containers/inbound-tickets/components/AxleWeightsRow'
import AxleWeightsHeader from 'components/containers/inbound-tickets/components/AxleWeightsHeader'
import DialogModal from 'components/generic/modals/dialog-modal'
import MultiImageBlade from 'components/generic/modals/blade-modal/MultiImageBlade'

const AxleWeightsModal = ({ onClose, open, materialData, onSave, disabled, ...restProps }) => {
  const methods = useForm()
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = methods
  const isDirty = !!Object.keys(dirtyFields).length

  const [numberOfAxles, setNumberOfAxles] = useState(1)
  const [axleRefs, setAxleRefs] = useState({})
  const [multiImageBladeData, setMultiImageBladeData] = useState(null)

  useEffect(() => {
    if (open) {
      const axleWeights = materialData?.axleWeights || []
      const currentNumberOfAxles = axleWeights?.length || 1
      setNumberOfAxles(currentNumberOfAxles)

      const resetPayload = { axles: {} }

      if (currentNumberOfAxles === 1) {
        resetPayload.axles['axle-1'] = {
          gross: materialData?.gross || 0,
          tare: materialData?.tare || 0,
          grossImages: materialData?.grossImages || [],
          tareImages: materialData?.tareImages || [],
          isManualGross: materialData?.isManualGross || false,
          isManualTare: materialData?.isManualTare || false,
        }
      } else {
        for (let i = 1; i <= currentNumberOfAxles; i++) {
          const currentAxleData = axleWeights?.find(axleData => axleData.axleNumber === i)
          resetPayload.axles[`axle-${i}`] = {
            gross: currentAxleData?.gross || 0,
            tare: currentAxleData?.tare || 0,
            grossImages: currentAxleData?.grossImages || [],
            tareImages: currentAxleData?.tareImages || [],
            isManualGross: currentAxleData?.isManualGross || false,
            isManualTare: currentAxleData?.isManualTare || false,
          }
        }
      }

      reset(resetPayload)
    }
  }, [materialData, open])

  useEffect(() => {
    axleRefs?.gross1?.current?.focus()
  }, [axleRefs, numberOfAxles])

  const onSubmit = data => {
    onSave(data, numberOfAxles)
    onClose()
  }

  return (
    <FormProvider {...methods}>
      <DialogModal
        {...restProps}
        hideTitle
        open={open}
        onClose={onClose}
        onSave={handleSubmit(onSubmit)}
        // enable save if isDirty OR if numberOfAxles was changed
        saveDisabled={!(isDirty || (materialData?.axleWeights || []).length !== numberOfAxles)}
        dialogStyles={{
          '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
        }}
        saveButtonProps={{
          variant: 'outlined',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <AxleWeightsHeader numberOfAxles={numberOfAxles} setNumberOfAxles={setNumberOfAxles} disabled={disabled} />
          <Stack spacing={2}>
            {Array(numberOfAxles)
              .fill()
              .map((_, index) => (
                <AxleWeightsRow
                  key={`axle-${index + 1}-gross`}
                  type="gross"
                  axleNumber={index + 1}
                  numberOfAxles={numberOfAxles}
                  setAxleRefs={setAxleRefs}
                  setMultiImageBladeData={setMultiImageBladeData}
                  disabled={disabled}
                  materialData={materialData}
                />
              ))}
            {Array(numberOfAxles)
              .fill()
              .map((_, index) => (
                <AxleWeightsRow
                  key={`axle-${index + 1}-tare`}
                  type="tare"
                  axleNumber={index + 1}
                  numberOfAxles={numberOfAxles}
                  setAxleRefs={setAxleRefs}
                  setMultiImageBladeData={setMultiImageBladeData}
                  disabled={disabled}
                  materialData={materialData}
                />
              ))}
          </Stack>
        </form>
        <MultiImageBlade
          sx={{ zIndex: 1301 }}
          multiImageBladeData={multiImageBladeData}
          setMultiImageBladeData={setMultiImageBladeData}
          generateSaveImagesFunction={imageType => imageData => {
            methods.setValue(`axles.axle-${multiImageBladeData?.axleNumber}.${imageType}Images`, imageData, {
              shouldDirty: true,
            })
            setMultiImageBladeData(prevMultiImageBladeData => {
              const { axleNumber, axleWeights: previousAxleWeights } = prevMultiImageBladeData
              const axleData = previousAxleWeights?.find(axle => axle.axleNumber === axleNumber)
              const updatedAxleWeights = previousAxleWeights.filter(axle => axle.axleNumber !== axleNumber)
              updatedAxleWeights.push({
                ...axleData,
                [`${imageType}Images`]: imageData,
              })
              return {
                ...prevMultiImageBladeData,
                axleWeights: updatedAxleWeights,
                [`${imageType}Images`]: imageData,
              }
            })
          }}
          disabledUploadImages={disabled}
        />
      </DialogModal>
    </FormProvider>
  )
}

AxleWeightsModal.propTypes = {
  ...DialogModal.propTypes,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  materialData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

AxleWeightsModal.defaultProps = {
  ...DialogModal.defaultProps,
  onClose: () => {},
  open: false,
  materialData: null,
  disabled: false,
}

export default AxleWeightsModal
